import React from "react";
import { COLORS, TYPOGRAPHY, SPACING, SHADOWS, RADIUS, TRANSITIONS, GRADIENTS } from '../constants/designTokens';

const steps = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
        <path d="M12 13l8-5v10H4V8l8 5z" opacity="0.6"/>
        <circle cx="6" cy="10" r="1" opacity="0.8"/>
        <circle cx="18" cy="10" r="1" opacity="0.8"/>
        <path d="M2 2h20v1H2zm0 19h20v1H2z" opacity="0.3"/>
      </svg>
    ),
    title: "Submit Your Inquiry",
    desc: "Reach out to us with your parts list, vessel details, or equipment brand and model."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        <circle cx="9" cy="11" r="1" fill="white"/>
        <circle cx="12" cy="11" r="1" fill="white"/>
        <circle cx="15" cy="11" r="1" fill="white"/>
        <path d="M7 7h10v1H7zm0 2h8v1H7zm0 2h6v1H7z" fill="white" opacity="0.7"/>
        <path d="M12 18l-2-2H4V4h16v12h-6l-2 2z" opacity="0.4"/>
      </svg>
    ),
    title: "Consultation",
    desc: "We'll discuss your requirements and ask a few key questions to correctly identify the parts you need."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        <circle cx="12" cy="9" r="2" fill="white"/>
        <path d="M12 22s7-7.75 7-13" opacity="0.5"/>
        <path d="M5 9c0 5.25 7 13 7 13" opacity="0.5"/>
        <circle cx="12" cy="20" r="1" opacity="0.6"/>
        <circle cx="10" cy="18" r="0.5" opacity="0.4"/>
        <circle cx="14" cy="18" r="0.5" opacity="0.4"/>
      </svg>
    ),
    title: "Part Sourcing & Verification",
    desc: "We locate and verify the right spare parts, cross-checking specifications against your equipment and compliance requirements."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2V8l-4-6z"/>
        <path d="M14 2l4 6h-4V2z" opacity="0.7"/>
        <path d="M6 10h8v1H6zm0 2h8v1H6zm0 2h5v1H6z" fill="white" opacity="0.8"/>
        <path d="M16 12h2v1h-2zm0 2h2v1h-2z" fill="white" opacity="0.6"/>
        <circle cx="8" cy="16" r="0.5" fill="white"/>
        <circle cx="10" cy="18" r="0.5" fill="white"/>
        <path d="M12 16h4v1h-4z" fill="white" opacity="0.8"/>
      </svg>
    ),
    title: "Transparent Quotation",
    desc: "Receive a clear, competitive quote for the recommended parts and delivery timeline."
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
        <path d="M9 6L6 9l-4.4-4.3c-.4.8-.6 1.7-.6 2.6 0 1.6.6 3.1 1.7 4.2 1.1 1.1 2.6 1.7 4.2 1.7.9 0 1.8-.2 2.6-.6L9 6z" opacity="0.6"/>
        <circle cx="7" cy="7" r="1.5" fill="white" opacity="0.8"/>
        <path d="M14 14l7 7" stroke="white" strokeWidth="1" opacity="0.5"/>
        <path d="M19 19l2 2" stroke="white" strokeWidth="0.8" opacity="0.3"/>
      </svg>
    ),
    title: "Global Delivery",
    desc: "We handle packaging, documentation, and logistics to deliver your parts to any port worldwide, on time."
  }
];

const OurProcess = () => (
  <section id="our-process" className="ourprocess-section py-5" style={{
    background: `linear-gradient(135deg, #0a1828 0%, ${COLORS.primary.dark} 50%, #0f2940 100%)`,
    position: "relative",
    overflow: "hidden"
  }}>
    <style>{`
      @keyframes float {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-10px);
        }
      }

      @keyframes glow {
        0%, 100% {
          box-shadow: 0 0 20px rgba(64, 168, 196, 0.3), 0 0 40px rgba(64, 168, 196, 0.1);
        }
        50% {
          box-shadow: 0 0 30px rgba(64, 168, 196, 0.5), 0 0 60px rgba(64, 168, 196, 0.2);
        }
      }

      @keyframes slideInLeft {
        from {
          opacity: 0;
          transform: translateX(-50px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(50px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      .ourprocess-section::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -10%;
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(64, 168, 196, 0.15) 0%, transparent 70%);
        border-radius: ${RADIUS.full};
        animation: float 8s ease-in-out infinite;
      }

      .ourprocess-section::after {
        content: '';
        position: absolute;
        bottom: -50%;
        right: -10%;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(64, 168, 196, 0.1) 0%, transparent 70%);
        border-radius: ${RADIUS.full};
        animation: float 10s ease-in-out infinite reverse;
      }

      .ourprocess-section h2 {
        color: ${COLORS.text.white};
        font-weight: 800;
        margin-bottom: ${SPACING.md};
        text-align: center;
        position: relative;
        font-size: ${TYPOGRAPHY.fontSize['3xl']};
        text-transform: uppercase;
        letter-spacing: 2px;
        z-index: 2;
      }

      .ourprocess-section .subtitle {
        color: ${COLORS.primary.teal};
        text-align: center;
        font-size: ${TYPOGRAPHY.fontSize.md};
        margin-bottom: ${SPACING['4xl']};
        position: relative;
        z-index: 2;
        font-weight: ${TYPOGRAPHY.fontWeight.normal};
      }
      .process-timeline {
        position: relative;
        max-width: 900px;
        margin: 0 auto;
        z-index: 2;
      }
      .process-timeline::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 2px;
        background: linear-gradient(180deg,
          transparent 0%,
          rgba(64, 168, 196, 0.3) 10%,
          rgba(64, 168, 196, 0.6) 50%,
          rgba(64, 168, 196, 0.3) 90%,
          transparent 100%);
        transform: translateX(-50%);
        z-index: 1;
      }
      .process-step {
        position: relative;
        margin: ${SPACING['4xl']} 0;
        display: flex;
        align-items: center;
        z-index: 2;
      }
      .process-step:nth-child(odd) {
        flex-direction: row;
      }
      .process-step:nth-child(even) {
        flex-direction: row-reverse;
      }
      .process-card {
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid rgba(64, 168, 196, 0.2);
        border-radius: ${RADIUS['2xl']};
        padding: ${SPACING['2xl']} ${SPACING.xl};
        width: calc(50% - 50px);
        position: relative;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: ${SHADOWS.lg};
      }
      .process-card:hover {
        transform: translateY(-8px) scale(1.02);
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(64, 168, 196, 0.5);
        box-shadow: 0 20px 60px rgba(64, 168, 196, 0.3);
      }
      .process-card::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: ${RADIUS['2xl']};
        padding: 1px;
        background: linear-gradient(135deg, rgba(64, 168, 196, 0.4), transparent);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        opacity: 0;
        transition: opacity 0.5s ease;
      }
      .process-card:hover::after {
        opacity: 1;
      }
      .process-number {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 70px;
        height: 70px;
        background: ${GRADIENTS.teal};
        border: 5px solid rgba(8, 32, 50, 0.8);
        border-radius: ${RADIUS.full};
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${COLORS.text.white};
        font-weight: 900;
        font-size: ${TYPOGRAPHY.fontSize.xl};
        box-shadow: 0 0 0 0 rgba(64, 168, 196, 0.4);
        z-index: 3;
        transition: ${TRANSITIONS.all};
        animation: glow 2s ease-in-out infinite;
      }
      .process-step:hover .process-number {
        transform: translate(-50%, -50%) scale(1.15) rotate(360deg);
        box-shadow: 0 0 40px rgba(64, 168, 196, 0.8);
      }
      .process-icon {
        background: linear-gradient(135deg, rgba(64, 168, 196, 0.2) 0%, rgba(46, 140, 160, 0.1) 100%);
        border: 2px solid rgba(64, 168, 196, 0.3);
        border-radius: ${RADIUS.full};
        width: 60px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${COLORS.primary.teal};
        font-size: 1.5rem;
        margin-bottom: ${SPACING.lg};
        transition: ${TRANSITIONS.all};
      }
      .process-card:hover .process-icon {
        transform: scale(1.1) rotate(5deg);
        background: linear-gradient(135deg, rgba(64, 168, 196, 0.3) 0%, rgba(46, 140, 160, 0.2) 100%);
        border-color: rgba(64, 168, 196, 0.6);
        box-shadow: 0 10px 30px rgba(64, 168, 196, 0.3);
      }
      .process-title {
        font-size: ${TYPOGRAPHY.fontSize.lg};
        font-weight: ${TYPOGRAPHY.fontWeight.bold};
        color: ${COLORS.text.white};
        margin-bottom: ${SPACING.md};
        line-height: ${TYPOGRAPHY.lineHeight.tight};
        letter-spacing: 0.5px;
      }
      .process-desc {
        color: rgba(255, 255, 255, 0.8);
        font-size: ${TYPOGRAPHY.fontSize.base};
        line-height: ${TYPOGRAPHY.lineHeight.relaxed};
        margin: 0;
      }
      
      @media (max-width: 991px) {
        .ourprocess-section h2 {
          font-size: ${TYPOGRAPHY.fontSize['2xl']};
        }
        .process-card {
          width: calc(50% - 60px);
        }
      }

      @media (max-width: 768px) {
        .ourprocess-section h2 {
          font-size: ${TYPOGRAPHY.fontSize['2xl']};
        }
        .process-timeline::before {
          left: 40px;
        }
        .process-step {
          flex-direction: row !important;
          margin: ${SPACING['2xl']} 0;
        }
        .process-card {
          width: calc(100% - 100px);
          margin-left: 80px;
          padding: ${SPACING.xl} ${SPACING.lg};
        }
        .process-number {
          left: 40px;
          width: 65px;
          height: 65px;
          font-size: ${TYPOGRAPHY.fontSize.lg};
        }
        .process-icon {
          width: 55px;
          height: 55px;
        }
        .process-title {
          font-size: ${TYPOGRAPHY.fontSize.lg};
        }
      }

      @media (max-width: 576px) {
        .ourprocess-section h2 {
          font-size: ${TYPOGRAPHY.fontSize.xl};
          letter-spacing: 1px;
        }
        .ourprocess-section .subtitle {
          font-size: ${TYPOGRAPHY.fontSize.base};
        }
        .process-card {
          width: calc(100% - 90px);
          margin-left: 70px;
          padding: ${SPACING.lg} ${SPACING.md};
        }
        .process-number {
          left: 35px;
          width: 60px;
          height: 60px;
          font-size: ${TYPOGRAPHY.fontSize.lg};
        }
        .process-timeline::before {
          left: 35px;
        }
      }

      @media (max-width: 480px) {
        .ourprocess-section h2 {
          font-size: ${TYPOGRAPHY.fontSize.lg};
        }
        .ourprocess-section .subtitle {
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
        .process-card {
          width: calc(100% - 75px);
          margin-left: 60px;
          padding: ${SPACING.lg} ${SPACING.md};
        }
        .process-number {
          width: 55px;
          height: 55px;
          font-size: ${TYPOGRAPHY.fontSize.md};
          left: 30px;
        }
        .process-timeline::before {
          left: 30px;
        }
        .process-title {
          font-size: ${TYPOGRAPHY.fontSize.md};
        }
        .process-desc {
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
        .process-icon {
          width: 50px;
          height: 50px;
          font-size: 1.3rem;
        }
      }
    `}</style>
    <div className="container">
      <h2>Our Process</h2>
      <p className="subtitle">From inquiry to delivery, we make it seamless</p>
      <div className="process-timeline">
        {steps.map((step, idx) => (
          <div
            className={`process-step ${idx % 2 === 0 ? 'slide-in-left' : 'slide-in-right'}`}
            key={idx}
          >
            <div className="process-card">
              <div className="process-icon">
                {step.icon}
              </div>
              <div className="process-title">{step.title}</div>
              <p className="process-desc">{step.desc}</p>
            </div>
            <div className="process-number">{idx + 1}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default OurProcess;
